import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileSearch,
  Info,
  ShieldAlert,
  Users,
} from "lucide-react";

export interface FindingContext {
  finding_title?: string | null;
  risk_level?: string | null;
  priority_level?: string | null;
  timeframe?: string | null;
  owner?: string | null;
  owners?: string[] | null;
  due_date?: string | null;
  recommended_action?: string | null;
  recommended_measures?: string | null;
  remediation_path?: string | null;
  justification?: string | null;
  observed_gap?: string | null;
  recommended_additional_evidence?: string | null;
  evidence_reviewed?: string | null;
  csv_domain?: string | null;
  nist_function?: string | null;
  control_code?: string | null;
}

const RISK_COLORS: Record<string, string> = {
  Critical: "bg-red-700 text-white",
  HIGH: "bg-destructive text-destructive-foreground",
  High: "bg-destructive text-destructive-foreground",
  MODERATE: "bg-yellow-500/90 text-white",
  Moderate: "bg-yellow-500/90 text-white",
  LOW: "bg-green-600 text-white",
  Low: "bg-green-600 text-white",
};

function splitChips(input?: string | null): string[] {
  if (!input) return [];
  return input
    .split(/\r?\n|;|•|·|\u2022/)
    .map((s) => s.replace(/^[-*\s]+/, "").trim())
    .filter(Boolean);
}

function Field({
  label,
  value,
  icon: Icon,
  fullWidth,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  fullWidth?: boolean;
}) {
  return (
    <div className={fullWidth ? "col-span-full" : undefined}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-0.5">
        {Icon ? <Icon className="h-3 w-3" /> : null}
        {label}
      </div>
      <div className="text-xs text-foreground whitespace-pre-wrap">
        {value || <span className="text-muted-foreground italic">—</span>}
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="col-span-full text-[10px] uppercase tracking-wide font-semibold text-foreground border-b pb-1">
      {title}
    </div>
  );
}

function EvidenceChips({ value }: { value?: string | null }) {
  const chips = splitChips(value);
  if (chips.length === 0) {
    return <p className="text-xs text-muted-foreground italic">—</p>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((c, i) => (
        <Badge key={`${c}-${i}`} variant="outline" className="text-[10px]">
          {c}
        </Badge>
      ))}
    </div>
  );
}

interface Props {
  finding: FindingContext;
  /** "respondent" hides Reviewer Assessment in a collapsible; "reviewer" shows everything inline with section headers. */
  variant?: "respondent" | "reviewer";
  /** When true (respondent variant), omit the Reviewer Assessment collapsible so it can be rendered separately. */
  hideReviewerAssessment?: boolean;
}

export const ReviewerAssessmentPanel: React.FC<{
  finding: FindingContext;
  defaultOpen?: boolean;
  hideHeader?: boolean;
}> = ({ finding, defaultOpen = false, hideHeader = false }) => {
  const [open, setOpen] = useState(defaultOpen || hideHeader);

  const body = (
    <div className={hideHeader ? "grid grid-cols-1 gap-3" : "px-3 pb-3 pt-1 grid grid-cols-1 gap-3"}>
      <Field label="Observed Gap" icon={AlertTriangle} value={finding.observed_gap} fullWidth />
      <Field label="Justification / Rationale" icon={Info} value={finding.justification} fullWidth />
      <div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1">
          <FileSearch className="h-3 w-3" />
          Evidence to Review
        </div>
        <EvidenceChips value={finding.evidence_reviewed} />
      </div>
    </div>
  );

  if (hideHeader) return body;

  return (
    <section className="rounded-md border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-semibold"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
        Reviewer Assessment
        <span className="text-[10px] font-normal text-muted-foreground ml-1">
          (why this finding was raised)
        </span>
      </button>
      {open && body}
    </section>
  );
};



export const FindingContextBlock: React.FC<Props> = ({ finding, variant = "respondent", hideReviewerAssessment = false }) => {
  const [showReviewerAssessment, setShowReviewerAssessment] = useState(false);

  const owners =
    Array.isArray(finding.owners) && finding.owners.length > 0
      ? finding.owners
      : finding.owner
      ? [finding.owner]
      : [];

  const domainParts = [finding.csv_domain, finding.nist_function].filter(Boolean).join(" · ");

  const header = (
    <header className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
          Finding Context (read-only)
        </div>
        <h4 className="text-sm font-semibold leading-snug">
          {finding.finding_title || (
            <span className="italic text-muted-foreground">Untitled finding</span>
          )}
        </h4>
        {(domainParts || finding.control_code) && (
          <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            {finding.control_code && <span className="font-mono">{finding.control_code}</span>}
            {domainParts && <span>{domainParts}</span>}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {finding.risk_level && (
          <Badge
            className={`text-[10px] gap-1 ${RISK_COLORS[finding.risk_level] ?? "bg-muted text-foreground"}`}
          >
            <ShieldAlert className="h-2.5 w-2.5" /> {finding.risk_level}
          </Badge>
        )}
        {finding.priority_level && (
          <Badge variant="outline" className="text-[10px]">{finding.priority_level}</Badge>
        )}
        {finding.timeframe && (
          <Badge variant="outline" className="text-[10px]">{finding.timeframe}</Badge>
        )}
      </div>
    </header>
  );

  const summaryGrid = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field
        label="Owner(s)"
        icon={Users}
        value={
          owners.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {owners.map((o) => (
                <Badge key={o} variant="secondary" className="text-[10px]">{o}</Badge>
              ))}
            </div>
          ) : null
        }
      />
      <Field
        label="Due Date"
        icon={Calendar}
        value={finding.due_date ? new Date(finding.due_date).toLocaleDateString() : null}
      />
    </div>
  );

  if (variant === "reviewer") {
    return (
      <section className="rounded-md border bg-muted/30 p-3 space-y-3">
        {header}
        {summaryGrid}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SectionHeader title="Finding Assessment" />
          <Field
            label="Observed Gap"
            icon={AlertTriangle}
            value={finding.observed_gap}
            fullWidth
          />
          <Field
            label="Justification / Rationale"
            icon={Info}
            value={finding.justification}
            fullWidth
          />

          <SectionHeader title="Evidence Expectations" />
          <Field
            label="Recommended Additional Evidence"
            icon={FileSearch}
            value={finding.recommended_additional_evidence}
            fullWidth
          />
          <div className="col-span-full">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1">
              <FileSearch className="h-3 w-3" />
              Evidence to Review
            </div>
            <EvidenceChips value={finding.evidence_reviewed} />
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground italic border-t pt-2">
          These fields are managed in the Findings tab. Edit there to update them.
        </p>
      </section>
    );
  }

  // Respondent variant — keep reviewer reasoning collapsed by default.
  return (
    <section className="rounded-md border bg-muted/30 p-3 space-y-3">
      {header}
      {summaryGrid}

      {!hideReviewerAssessment && (
        <div className="rounded-md border bg-card">
          <button
            type="button"
            onClick={() => setShowReviewerAssessment((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-semibold"
            aria-expanded={showReviewerAssessment}
          >
            {showReviewerAssessment ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
            Reviewer Assessment
            <span className="text-[10px] font-normal text-muted-foreground ml-1">
              (why this finding was raised)
            </span>
          </button>
          {showReviewerAssessment && (
            <div className="px-3 pb-3 pt-1 grid grid-cols-1 gap-3">
              <Field label="Observed Gap" icon={AlertTriangle} value={finding.observed_gap} fullWidth />
              <Field label="Justification / Rationale" icon={Info} value={finding.justification} fullWidth />
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1">
                  <FileSearch className="h-3 w-3" />
                  Evidence to Review
                </div>
                <EvidenceChips value={finding.evidence_reviewed} />
              </div>
            </div>
          )}
        </div>
      )}

    </section>
  );
};

export default FindingContextBlock;
